import type { InvoiceDocumentInput } from "~/utils/invoice-document.types";
import { workspaceLogoPublicUrl } from "~/utils/time-rounding";

const DEFAULT_BRAND_ACCENT = "#002c15";
const TOOLBAR_LIME = "#d5f24b";

function escapeHtml(value: string) {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character] ?? character,
  );
}

function brandAccentHex(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return DEFAULT_BRAND_ACCENT;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(withHash)) return DEFAULT_BRAND_ACCENT;
  return withHash;
}

function htmlLines(value: string | null) {
  return value ? escapeHtml(value).replaceAll("\n", "<br>") : "";
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

function formatDate(value: string | null) {
  if (!value) return "Not set";
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium" }).format(date);
}

function lineSubtotal(item: InvoiceDocumentInput["items"][number]) {
  return Math.round(Number(item.quantity) * item.unit_amount_cents);
}

function lineGst(item: InvoiceDocumentInput["items"][number]) {
  return Math.round(lineSubtotal(item) * (Number(item.gst_rate) / 100));
}

export function printInvoiceDocument({
  client,
  invoice,
  items,
  workspace,
}: InvoiceDocumentInput) {
  const popup = window.open("", "_blank");
  if (!popup) return false;
  popup.opener = null;

  const sender = workspace.legal_name || workspace.name;
  const accent = brandAccentHex(workspace.brand_accent_hex);
  const logoUrl = workspaceLogoPublicUrl(workspace.logo_path);
  const bankDetails = [
    workspace.bank_name,
    workspace.bank_account_name,
    workspace.bank_account_number,
  ].filter((value): value is string => Boolean(value?.trim()));
  const itemRows = items
    .map((item) => {
      const subtotal = lineSubtotal(item);
      const total = subtotal + lineGst(item);
      return `<tr>
        <td>${escapeHtml(item.description)}</td>
        <td class="number">${Number(item.quantity).toLocaleString("en-NZ")}</td>
        <td class="number">${formatMoney(item.unit_amount_cents, invoice.currency)}</td>
        <td class="number">${Number(item.gst_rate).toLocaleString("en-NZ")}%</td>
        <td class="number strong">${formatMoney(total, invoice.currency)}</td>
      </tr>`;
    })
    .join("");

  popup.document.write(`<!doctype html>
<html lang="en-NZ">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeHtml(invoice.invoice_number)} | ${escapeHtml(sender)}</title>
    <style>
      :root { color-scheme: light; font-family: Arial, Helvetica, sans-serif; color: #17211d; background: #fff; }
      * { box-sizing: border-box; }
      body { margin: 0; background: #eef2ef; }
      .toolbar { position: sticky; top: 0; display: flex; justify-content: flex-end; padding: 12px 20px; background: #002c15; }
      .toolbar button { min-height: 40px; padding: 0 18px; border: 0; border-radius: 6px; color: #002c15; background: ${TOOLBAR_LIME}; font: inherit; font-weight: 700; cursor: pointer; }
      .sheet { width: min(210mm, calc(100% - 32px)); min-height: 297mm; margin: 24px auto; padding: 18mm; background: #fff; box-shadow: 0 8px 28px rgba(0,44,21,.12); }
      .header { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 32px; padding-bottom: 28px; border-bottom: 3px solid ${accent}; }
      .brand-logo { display: block; max-height: 64px; max-width: 200px; margin-bottom: 12px; object-fit: contain; }
      h1 { margin: 0 0 10px; font-size: 28px; line-height: 1.15; color: ${accent}; }
      .sender, .muted { color: #52605a; font-size: 13px; line-height: 1.55; }
      .invoice-meta { text-align: right; }
      .invoice-label { color: #52605a; font-size: 12px; font-weight: 700; text-transform: uppercase; }
      .invoice-number { margin: 4px 0 12px; color: ${accent}; font-size: 24px; font-weight: 800; }
      .meta-row { display: flex; justify-content: flex-end; gap: 16px; margin-top: 5px; font-size: 13px; }
      .meta-row span:first-child { color: #52605a; }
      .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin: 28px 0; }
      .eyebrow { margin-bottom: 8px; color: #52605a; font-size: 11px; font-weight: 700; text-transform: uppercase; }
      .party-name { margin-bottom: 4px; font-weight: 700; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th { padding: 10px 8px; border-bottom: 2px solid ${accent}; color: #52605a; font-size: 11px; text-align: left; text-transform: uppercase; }
      td { padding: 13px 8px; border-bottom: 1px solid #dbe2de; font-size: 13px; vertical-align: top; overflow-wrap: anywhere; }
      th:first-child, td:first-child { width: 44%; padding-left: 0; }
      th:last-child, td:last-child { padding-right: 0; }
      .number { text-align: right; }
      .strong { font-weight: 700; }
      .totals { width: min(320px, 100%); margin: 20px 0 32px auto; }
      .total-row { display: flex; justify-content: space-between; gap: 24px; padding: 6px 0; font-size: 14px; }
      .total-row.grand { margin-top: 6px; padding-top: 12px; border-top: 2px solid ${accent}; color: ${accent}; font-size: 20px; font-weight: 800; }
      .details { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; padding-top: 24px; border-top: 1px solid #dbe2de; }
      .details p { margin: 0; font-size: 13px; line-height: 1.6; }
      .footer { margin-top: 38px; padding-top: 14px; border-top: 1px solid #dbe2de; color: #6a756f; font-size: 11px; }
      @media (max-width: 680px) { .sheet { width: 100%; min-height: 0; margin: 0; padding: 24px; box-shadow: none; } .header, .parties, .details { grid-template-columns: 1fr; } .invoice-meta { text-align: left; } .meta-row { justify-content: flex-start; } }
      @media print { @page { size: A4; margin: 0; } body { background: #fff; } .toolbar { display: none; } .sheet { width: 210mm; min-height: 297mm; margin: 0; padding: 16mm; box-shadow: none; } }
    </style>
  </head>
  <body>
    <div class="toolbar"><button type="button" onclick="window.print()">Print / save PDF</button></div>
    <main class="sheet">
      <header class="header">
        <div>
          ${logoUrl ? `<img class="brand-logo" src="${escapeHtml(logoUrl)}" alt="" />` : ""}
          <h1>${escapeHtml(sender)}</h1>
          <div class="sender">
            ${workspace.address ? `${htmlLines(workspace.address)}<br>` : ""}
            ${workspace.contact_email ? `${escapeHtml(workspace.contact_email)}<br>` : ""}
            ${workspace.phone ? escapeHtml(workspace.phone) : ""}
          </div>
        </div>
        <div class="invoice-meta">
          <div class="invoice-label">${workspace.gst_registered ? "Tax invoice" : "Invoice"}</div>
          <div class="invoice-number">${escapeHtml(invoice.invoice_number)}</div>
          <div class="meta-row"><span>Issued</span><strong>${formatDate(invoice.issue_date)}</strong></div>
          <div class="meta-row"><span>Due</span><strong>${formatDate(invoice.due_date)}</strong></div>
          ${invoice.purchase_order_reference ? `<div class="meta-row"><span>PO</span><strong>${escapeHtml(invoice.purchase_order_reference)}</strong></div>` : ""}
          ${invoice.client_reference ? `<div class="meta-row"><span>Client ref</span><strong>${escapeHtml(invoice.client_reference)}</strong></div>` : ""}
          ${invoice.job_starts_on || invoice.job_ends_on ? `<div class="meta-row"><span>Job</span><strong>${escapeHtml([invoice.job_starts_on, invoice.job_ends_on].filter(Boolean).join(" – "))}</strong></div>` : ""}
        </div>
      </header>
      <section class="parties">
        <div>
          <div class="eyebrow">Bill to</div>
          <div class="party-name">${escapeHtml(client?.name ?? "Client")}</div>
          <div class="muted">
            ${client?.contact_name ? `${escapeHtml(client.contact_name)}<br>` : ""}
            ${client?.billing_address ? `${htmlLines(client.billing_address)}<br>` : ""}
            ${client?.email ? escapeHtml(client.email) : ""}
          </div>
        </div>
        <div>
          <div class="eyebrow">Reference</div>
          <div class="party-name">${escapeHtml(invoice.invoice_number)}</div>
          <div class="muted">Status: ${escapeHtml(invoice.status)}</div>
        </div>
      </section>
      <table>
        <thead><tr><th>Description</th><th class="number">Qty</th><th class="number">Rate</th><th class="number">GST</th><th class="number">Total</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>
      <section class="totals">
        <div class="total-row"><span>Subtotal</span><strong>${formatMoney(invoice.subtotal_cents, invoice.currency)}</strong></div>
        <div class="total-row"><span>GST</span><strong>${formatMoney(invoice.gst_cents, invoice.currency)}</strong></div>
        ${invoice.rounding_adjustment_cents !== 0 ? `<div class="total-row"><span>Rounding</span><strong>${formatMoney(invoice.rounding_adjustment_cents, invoice.currency)}</strong></div>` : ""}
        <div class="total-row grand"><span>Total</span><span>${formatMoney(invoice.total_cents, invoice.currency)}</span></div>
      </section>
      <section class="details">
        <div>
          <div class="eyebrow">Payment</div>
          ${invoice.payment_instructions ? `<p>${htmlLines(invoice.payment_instructions)}</p>` : ""}
          ${bankDetails.length ? `<p>${bankDetails.map(escapeHtml).join("<br>")}</p>` : ""}
          ${!invoice.payment_instructions && !bankDetails.length ? '<p class="muted">No payment instructions supplied.</p>' : ""}
        </div>
        <div>
          <div class="eyebrow">Notes</div>
          ${invoice.notes ? `<p>${htmlLines(invoice.notes)}</p>` : '<p class="muted">No additional notes.</p>'}
        </div>
      </section>
      <footer class="footer">
        ${workspace.nzbn ? `NZBN ${escapeHtml(workspace.nzbn)}` : ""}
        ${workspace.nzbn && workspace.gst_number ? " &nbsp;|&nbsp; " : ""}
        ${workspace.gst_number ? `GST ${escapeHtml(workspace.gst_number)}` : ""}
      </footer>
    </main>
    <script>window.addEventListener("load", function () { window.setTimeout(function () { window.print(); }, 150); });</script>
  </body>
</html>`);
  popup.document.close();
  return true;
}
